<template>
  <MediaViewerModal
    :title="title"
    :download-url="downloadUrl"
    :media="document"
    :loading="loading"
    :error="error"
    loading-message="Loading document..."
    default-icon="fa-regular fa-file"
    :can-go-previous="canGoPrevious"
    :can-go-next="canGoNext"
    :go-previous="goPrevious"
    :go-next="goNext"
    @hide-modal="emit('hide-modal')"
    @retry="retryLoad"
  >
    <template #default="{ hiddenClass }">
      <!-- Image preview -->
      <img
        v-if="isImage"
        ref="imageRef"
        :src="documentUrl"
        :alt="document.name"
        class="media-viewer-element"
        :class="hiddenClass"
        @load="onLoaded"
        @error="onError"
      />

      <!-- PDF preview -->
      <iframe
        v-else-if="isPdf"
        ref="pdfRef"
        :src="documentUrl"
        class="media-viewer-element pdf-frame"
        :class="hiddenClass"
        @load="onLoaded"
      >
        <p class="media-viewer-pdf-fallback">
          Unable to display PDF.
          <a :href="documentUrl" target="_blank">Open in new tab</a>
        </p>
      </iframe>

      <!-- Unsupported format (Word docs, etc.) -->
      <div v-else-if="!loading" class="media-viewer-unsupported">
        <i class="fa-regular fa-file-word"></i>
        <p>Preview not available for this file type.</p>
        <SeraButton type="primary" @click="openInNewTab">
          <i class="fa-solid fa-external-link-alt mr-2"></i>
          Open Document
        </SeraButton>
      </div>
    </template>
  </MediaViewerModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import SeraButton from "@/components/Button.vue";
import MediaViewerModal from "@/components/MediaViewerModal.vue";
import { Document } from "@/typings/document";

const props = withDefaults(
  defineProps<{
    document: Document;
    canGoPrevious?: boolean;
    canGoNext?: boolean;
    goPrevious?: () => void;
    goNext?: () => void;
  }>(),
  {
    canGoPrevious: false,
    canGoNext: false,
    goPrevious: undefined,
    goNext: undefined
  }
);

const emit = defineEmits<{
  "hide-modal": [];
}>();

const imageRef = ref<HTMLImageElement | null>(null);
const pdfRef = ref<HTMLIFrameElement | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);

// Reset loading state when document changes
watch(
  () => props.document.id,
  () => {
    error.value = null;
    // Only show loading for images (PDFs/unsupported have unreliable load events)
    const ext =
      (props.document.name || "").split(".").pop()?.toLowerCase() || "";
    loading.value = ["jpg", "jpeg", "png"].includes(ext);
  }
);

const title = computed(() => props.document.name || "Document Viewer");

const documentUrl = computed(() => {
  return props.document.url || props.document.file?.url || "";
});

const downloadUrl = computed(() => {
  return props.document.download_url || documentUrl.value;
});

const fileExtension = computed(() => {
  const name = props.document.name || "";
  const ext = name.split(".").pop()?.toLowerCase() || "";
  return ext;
});

const isImage = computed(() => {
  return ["jpg", "jpeg", "png"].includes(fileExtension.value);
});

const isPdf = computed(() => {
  return fileExtension.value === "pdf";
});

function onLoaded() {
  loading.value = false;
  error.value = null;
}

function onError() {
  error.value = "Error loading document. The file may be unavailable.";
  loading.value = false;
}

function retryLoad() {
  loading.value = true;
  error.value = null;

  if (isImage.value && imageRef.value) {
    const currentSrc = imageRef.value.src;
    imageRef.value.src = "";
    imageRef.value.src = currentSrc;
  } else if (isPdf.value && pdfRef.value) {
    const currentSrc = pdfRef.value.src;
    pdfRef.value.src = "";
    pdfRef.value.src = currentSrc;
  }
}

function openInNewTab() {
  window.open(documentUrl.value, "_blank");
}

onMounted(() => {
  // For PDFs and unsupported formats, don't show loading state
  // (object tag load event is unreliable)
  if (!isImage.value) {
    loading.value = false;
  }
});

defineExpose({
  loading,
  error,
  documentUrl,
  downloadUrl,
  isImage,
  isPdf,
  fileExtension
});
</script>
